'use client';

import { usePathname } from 'next/navigation';
import { useFragment } from 'react-relay';
import { Markdown, useTranslation } from '@/helpers/translate';
import { useWasUrl } from '@/helpers/router';
import { useZusUserConnectedStore } from '@/helpers/user-auth.js';
import { fragmentCorpoData } from './layoutCorpoData';
import { tierIconsCompact } from '@/components/icons/hall-of-fame-tiers';
import HofIcon from '@/components/icons/hof';
import HofPlusIcon from '@/components/icons/hofplus';

export const FIDELITY_LEVELS = [
  {
    value: 5, nameKey: 'corpo:fidelity.level5Name', percent: 5, iconKey: 'scout',
  },
  {
    value: 10, nameKey: 'corpo:fidelity.level10Name', percent: 10, iconKey: 'blockbuster',
  },
  {
    value: 15, nameKey: 'corpo:fidelity.level15Name', percent: 15, iconKey: 'legend',
  },
];

// WEA-157 — the stepper shows the 3 earned tiers only (Scout/Blockbuster/Legend).
// Legend+ (the HOF subscription) is NOT a tier: it is promoted as a dedicated teaser
// card below, shown only to corpos without a running subscription (subscribed corpos
// get their own design, TBD).
const DISPLAY_TIERS = FIDELITY_LEVELS;

const LEGEND_MAINTAIN_THRESHOLD = 30;
// Remise maximale de l'abonnement Legend+ : rendue par le code, pas par la clé i18n
// `legendPlusTeaserMaxDiscount` dont la valeur (-50 %) n'est plus à jour.
export const LEGEND_PLUS_MAX_DISCOUNT = 30;

const LOCALE_BY_LANG = {
  fr: 'fr-FR',
  en: 'en-GB',
  es: 'es-ES',
};

const formatFidelityDeadline = ({ validUntil, year, lang = 'fr' }) => {
  if (validUntil) {
    const date = new Date(validUntil);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString(LOCALE_BY_LANG[lang] || LOCALE_BY_LANG.fr);
    }
  }
  return `31/12/${year}`;
};

// WEA-157 Legend+ — next charge, formatted from the corpo subscription snapshot (no Stripe call:
// amount + date are maintained on the corpo by the api-payments webhook).
const formatLegendPlusAmount = ({ amountExclTax, currency, lang = 'fr' }) => {
  if (amountExclTax == null) return null;
  const locale = LOCALE_BY_LANG[lang] || LOCALE_BY_LANG.fr;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: (currency || 'eur').toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amountExclTax);
  } catch (err) {
    return `${amountExclTax} €`;
  }
};

const formatLegendPlusDate = ({ nextChargeAt, lang = 'fr' }) => {
  if (!nextChargeAt) return null;
  const date = new Date(nextChargeAt);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(LOCALE_BY_LANG[lang] || LOCALE_BY_LANG.fr, {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};

const getFidelityProgressMessageKey = ({
  currentLevel,
  fidelityProgress,
  lang = 'fr',
}) => {
  if (!fidelityProgress) return null;

  const {
    year,
    yearBookings,
    bookingsToNextTier,
    bookingsToMaintain,
    validUntil,
  } = fidelityProgress;
  // Passage des paliers : le comptage suit l'année civile → deadline = 31/12 de l'année en cours.
  // `validUntil` (validité du statut acquis) ne sert que pour le maintien du statut Légende.
  const nextTierDeadline = formatFidelityDeadline({ year, lang });
  const maintainDeadline = formatFidelityDeadline({ validUntil, year, lang });

  if (currentLevel === 15) {
    if (yearBookings >= LEGEND_MAINTAIN_THRESHOLD) {
      return {
        key: 'corpo:fidelity.legendSecured',
        values: { year: year + 1 },
      };
    }
    if (bookingsToMaintain <= 0) return null;
    return {
      key: 'corpo:fidelity.legendMaintain',
      values: { count: bookingsToMaintain, deadline: maintainDeadline },
    };
  }

  if (currentLevel === 10) {
    if (bookingsToNextTier <= 0) return null;
    return {
      key: 'corpo:fidelity.progressToLegend',
      values: { count: bookingsToNextTier, deadline: nextTierDeadline },
    };
  }

  if (currentLevel === 5) {
    if (bookingsToNextTier <= 0) return null;
    return {
      key: 'corpo:fidelity.progressToBlockbuster',
      values: { count: bookingsToNextTier, deadline: nextTierDeadline },
    };
  }

  if (bookingsToNextTier <= 0) return null;
  return {
    key: 'corpo:fidelity.progressToScout',
    values: { count: bookingsToNextTier, deadline: nextTierDeadline },
  };
};

export default function FidelityProgramCard({ showDescription = false, dark }) {
  const { t, lang } = useTranslation();
  const userData = useZusUserConnectedStore((state) => state.userData);
  const corpoData = useFragment(fragmentCorpoData, userData?.corpo ?? null);
  const fidelityProgress = corpoData?.fidelityProgress;
  // WEA-157 Legend: an active HOF subscription shows Legend by default (reversible — when the
  // subscription lapses or hits a payment issue, status is no longer active/trialing and the card
  // falls back to the earned fidelity tier).
  const isLegendSubscribed = ['active', 'trialing'].includes(corpoData?.subscription?.status);
  const earnedLevel = corpoData?.isFidelityActive
    && corpoData?.fidelityLevel != null
    && [5, 10, 15].includes(corpoData.fidelityLevel)
    ? corpoData.fidelityLevel
    : null;
  const currentLevel = isLegendSubscribed ? 15 : earnedLevel;
  // Stepper spans the 3 earned tiers: an active subscription grants the Legend baseline
  // (15%), so it lights up Legend; otherwise the earned fidelity tier.
  const earnedIndex = earnedLevel != null ? FIDELITY_LEVELS.findIndex((l) => l.value === earnedLevel) : -1;
  const activeIndex = isLegendSubscribed ? FIDELITY_LEVELS.length - 1 : earnedIndex;
  const { getWasUrl } = useWasUrl({ lang });
  const legendHref = getWasUrl('/corpo/legend');
  const nextChargeAmount = formatLegendPlusAmount({
    amountExclTax: corpoData?.subscription?.amountExclTax,
    currency: corpoData?.subscription?.currency,
    lang,
  });
  const nextChargeDate = formatLegendPlusDate({
    nextChargeAt: corpoData?.subscription?.nextChargeAt,
    lang,
  });
  const isCanceling = !!corpoData?.subscription?.cancelAtPeriodEnd;
  // Don't tease the Legend+ page to someone already on it. legendHref carries the locale prefix
  // (getWasUrl), so comparing it to the current pathname works for fr/en/es alike.
  const pathname = usePathname();
  const stripTrailingSlash = (p) => (p || '').replace(/\/+$/, '');
  const isOnLegendPage = stripTrailingSlash(pathname) === stripTrailingSlash(legendHref);
  // Corpo space (compact banner next to the corpo name, showDescription=false): a subscribed corpo
  // only needs the Legend+ title bar — the full subscription detail lives in the Legend+ tab.
  const titleOnly = isLegendSubscribed && !showDescription;
  // Legend via subscription is unconditional → the "bookings to maintain/next tier" message does
  // not apply; only show the tier-progression message when the level is earned via bookings.
  const progressMessage = isLegendSubscribed ? null : getFidelityProgressMessageKey({
    currentLevel,
    fidelityProgress,
    lang,
  });

  if (!corpoData) return null;

  // Tous les fonds qui accueillent ce panneau sont blancs désormais ; `dark`
  // reste disponible si un écran sombre réapparaît. `showDescription` ne commande
  // plus que l'affichage du paragraphe explicatif.
  const light = !dark;
  const ui = light ? {
    shellCard: 'rounded-xl border border-was-ink/8 bg-white px-5 py-4 shadow-[0_1px_2px_rgba(19,14,27,0.04),0_18px_40px_-18px_rgba(19,14,27,0.28)]',
    iconOn: 'bg-was-violet/10 text-was-violet',
    iconOff: 'bg-was-ink/5 text-was-muted [&_svg]:grayscale [&_svg]:opacity-60',
    labelOn: 'text-was-ink',
    labelOff: 'text-was-muted',
    rail: 'bg-was-ink/10',
    railOn: 'bg-was-violet',
    hof: 'text-was-violet',
    text: 'text-was-ink',
    muted: 'text-was-slate',
  } : {
    shellCard: 'rounded-xl bg-black/40 px-5 py-4 backdrop-blur-sm',
    iconOn: 'bg-was-violet/15 text-was-violet',
    iconOff: 'bg-white/5 text-white/30 [&_svg]:grayscale [&_svg]:opacity-70',
    labelOn: 'text-was-violet',
    labelOff: 'text-white/30',
    rail: 'bg-white/15',
    railOn: 'bg-was-violet',
    hof: 'text-was-violet',
    text: 'text-white',
    muted: 'text-white/70',
  };

  // WEA-166 lisait `currentIndex` (l'index du palier merite). WEA-157 l'a scinde en
  // earnedIndex / activeIndex : on prend `activeIndex`, le seul qui tienne compte de
  // l'abonnement Legend+ (abonne => Legend allume, donc pas de palier "suivant").
  const current = activeIndex >= 0 ? FIDELITY_LEVELS[activeIndex] : null;
  const next = FIDELITY_LEVELS[activeIndex + 1] || null;

  if (!showDescription) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2.5 text-[13px]">
          {current ? (
            <>
              <span className={`flex size-5 shrink-0 items-center justify-center [&>svg]:size-4 ${ui.hof}`} aria-hidden="true">
                {tierIconsCompact[current.iconKey]}
              </span>
              <span className={`font-medium ${ui.text}`}>{t(current.nameKey)}</span>
              <span className={ui.muted}>{t('corpo:fidelity.discountPercent', { percent: current.percent })}</span>
            </>
          ) : (
            <span className={ui.muted}>{t(FIDELITY_LEVELS[0].nameKey)}</span>
          )}
          {next ? (
            <span className={`ml-auto ${ui.muted}`}>
              {t(next.nameKey)}
              {' '}
              {t('corpo:fidelity.discountPercent', { percent: next.percent })}
            </span>
          ) : null}
        </div>
        <div className="flex gap-1" aria-hidden="true">
          {FIDELITY_LEVELS.map((level) => {
            const isReached = currentLevel != null && currentLevel >= level.value;
            return <div key={level.value} className={`h-1 flex-1 rounded-full transition-colors ${isReached ? ui.railOn : ui.rail}`} />;
          })}
        </div>
        {progressMessage ? (
          <p className={`m-0 text-[12px] leading-snug ${ui.muted}`}>
            <Markdown>{t(progressMessage.key, progressMessage.values)}</Markdown>
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className={titleOnly ? '' : `px-5 py-3 ${showDescription ? ui.shellCard : 'rounded-xl border border-was-ink/8 bg-white'}`}>
      {/* Earned-tier rail (Scout → Legend) + explanatory copy: only meaningful while the corpo is
          climbing the tiers. A subscribed corpo is Legend by definition -> only the Legend+ card. */}
      {!isLegendSubscribed && (
      <div className={showDescription ? '' : 'flex gap-5 items-center'}>
        {!showDescription && (
        <span className={`mx-4 shrink-0 ${ui.hof}`}>
          <HofIcon className="size-11" />
        </span>
        )}
        <div className="flex-1">
          {/* Labels au-dessus avec icônes */}
          <div className="flex justify-between items-end px-1 mb-1.5">
            {DISPLAY_TIERS.map((level, i) => {
              const isReached = activeIndex >= i;
              let align = 'items-center';
              if (i === 0) align = 'items-start';
              else if (i === DISPLAY_TIERS.length - 1) align = 'items-end';
              return (
                <div
                  key={level.value}
                  className={`flex flex-col gap-1 ${align}`}
                >
                  <div className={`flex justify-center items-center rounded-lg size-8 ${isReached ? ui.iconOn : ui.iconOff}`}>
                    {tierIconsCompact[level.iconKey]}
                  </div>
                  <span className={`text-sm font-medium ${isReached ? ui.labelOn : ui.labelOff}`}>{t(level.nameKey)}</span>
                  <span className={`text-xs font-medium ${isReached ? ui.labelOn : ui.labelOff}`}>
                    {t('corpo:fidelity.discountPercent', { percent: level.percent })}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Jauge en 3 traits (un par palier), meme rendu que le bandeau compact de l'espace corpo */}
          <div className="flex gap-1 px-1 mb-4" aria-hidden="true">
            {DISPLAY_TIERS.map((level, i) => {
              const isReached = activeIndex >= i;
              return <div key={level.value} className={`h-1 flex-1 rounded-full transition-colors ${isReached ? ui.railOn : ui.rail}`} />;
            })}
          </div>

          <div>
            {progressMessage && (
            <div>
              <p className={`m-0 text-[15px] leading-relaxed ${ui.text}`}><Markdown>{t(progressMessage.key, progressMessage.values)}</Markdown></p>
            </div>
            )}
            {showDescription && (
            <p className={`mt-2 text-[13px] leading-relaxed ${ui.muted}`}>
              {t('corpo:fidelity.description')}
              {' '}
              {t('corpo:fidelity.description2')}
            </p>
            )}
          </div>
        </div>
      </div>
      )}

      {/* WEA-157 — Legend+ block. Two states sharing the same header (rouge WAS en teinte reduite,
          bg-red/10 : le violet est reserve aux paliers HOF merites) :
          - no running subscription -> teaser (pitch + "learn more" CTA)
          - running subscription    -> active state (benefits + next charge + manage link)
          The teaser is hidden on the Legend+ page itself (nothing to tease there). */}
      {!(isOnLegendPage && !isLegendSubscribed) && (
      <div className={`overflow-hidden rounded-xl border border-red/15 ${isLegendSubscribed ? '' : 'mt-4'}`}>
        {/* Header (common) */}
        <div className="flex gap-3 items-center px-3 py-2.5 bg-red/10">
          <span className="flex relative justify-center items-center rounded-lg size-9 shrink-0 bg-red/15 text-red">
            <HofPlusIcon className="size-5" />
          </span>
          <div className="flex flex-1 gap-2 items-center">
            <span className={`text-base font-medium ${ui.text}`}>
              {t('corpo:fidelity.levelLegendPlusName')}
            </span>
            <span className="px-2 py-0.5 text-[10px] font-medium tracking-wide uppercase rounded-full text-red bg-red/15">
              {t('corpo:fidelity.legendPlusTeaserBadge')}
            </span>
          </div>
          <div className="flex flex-col items-end shrink-0">
            <span className="text-[10px] font-medium tracking-wide uppercase text-red/80">
              {t('corpo:fidelity.legendPlusTeaserUpTo')}
            </span>
            <span className="text-xl font-medium leading-none text-red">
              {t('corpo:fidelity.discountPercent', { percent: LEGEND_PLUS_MAX_DISCOUNT })}
            </span>
          </div>
        </div>

        {!titleOnly && isLegendSubscribed && (
          <div className="p-4 bg-was-cream">
            <p className={`text-sm leading-relaxed ${ui.muted}`}>
              <Markdown>{t('corpo:fidelity.legendPlusActivePitch')}</Markdown>
            </p>
            {isCanceling && nextChargeDate ? (
              <p className={`mt-3 text-sm ${ui.muted}`}>
                {t('corpo:fidelity.legendPlusCanceledNotice', { date: nextChargeDate })}
              </p>
            ) : (nextChargeDate && (
              <div className="px-4 py-3 mt-4 rounded-xl border border-was-ink/8 bg-white">
                <span className="block text-[10px] font-medium tracking-wide uppercase text-was-muted">
                  {t('corpo:fidelity.legendPlusNextChargeLabel')}
                </span>
                <span className={`block mt-1 text-base font-medium ${ui.text}`}>
                  {nextChargeAmount
                    ? t('corpo:fidelity.legendPlusNextCharge', { amount: nextChargeAmount, date: nextChargeDate })
                    : nextChargeDate}
                </span>
              </div>
            ))}
            <a href={legendHref} className="inline-block mt-4 text-sm font-medium underline text-red hover:text-red-dark">
              {t('corpo:fidelity.legendPlusManageCta')}
            </a>
          </div>
        )}
        {!titleOnly && !isLegendSubscribed && (
          <a href={legendHref} className="block px-3 py-2.5 border-t border-red/15 bg-red/5 group">
            <div className="flex gap-3 justify-between items-center">
              <p className={`text-xs leading-relaxed ${ui.muted}`}>
                {t('corpo:fidelity.legendPlusTeaserPitch')}
              </p>
              <span className="flex gap-2 items-center text-sm font-medium whitespace-nowrap bg-red rounded-full text-white shrink-0 transition-colors group-hover:bg-red-dark px-3 py-1.5">
                {t('corpo:fidelity.legendPlusTeaserCta')}
                <span aria-hidden="true">→</span>
              </span>
            </div>
          </a>
        )}
      </div>
      )}
    </div>
  );
}
